import { Injectable } from '@angular/core';
import * as moment from 'moment';

import { LooseObject } from '../objects/loose-object';
import { environment } from '../../environments/environment';
import { blank, filled, get_text_color_from_hex, arr_some, fallback, data_get } from '../shared/utils/common';
import { get, isObject, toString, isEqual, lowerCase, chunk, isBoolean, isString, some, values, first, isArray, filter } from 'lodash-es';

import { FileService } from './file/file.service';

const { Liquid } = require("liquidjs");

/// This service contains liquid filters from fmcloud-docgenerator located at src/engine
@Injectable({
  providedIn: 'root'
})
export class LiquidFilterUtilsRegistrationService {
  private _engine: any;

  get engine(): any {
    return this._engine;
  }

  constructor(private _fileService: FileService) {
    this._engine = new Liquid();
  }

  registerFilters() {
    this._engine.registerFilter("data_get", (value, opts)  => {
      if (blank(value) || blank(opts)) {
        return value;
      }

      if (! isObject(opts)) {
        opts = {
          path: opts,
        };
      }

      return get(value, opts.path, opts.default_value);
    });

    this._engine.registerFilter("filled", (value) => filled(value));
    this._engine.registerFilter("blank", (value) => blank(value));
    this._engine.registerFilter("with_default", (value, defaultValue = null) => {
      defaultValue = filled(defaultValue) ? defaultValue : "--";

      if (blank(value)) {
        return defaultValue;
      }

      return value;
    });

    this.engine.registerFilter("to_presigned_url", async (value) => {
      if (blank(value)) {
        return null;
      }

      value = toString(value).replace(/\[[/]?image\]/ig, "");

      const url = await this._getURL(value);

      return url;
    });

    this._engine.registerFilter("compare_ignore_case", (value, compareTo) => isEqual(lowerCase(value), lowerCase(compareTo)));
    this._engine.registerFilter("compare_not_ignore_case", (value, compareTo) => ! isEqual(lowerCase(value), lowerCase(compareTo)));
    this._engine.registerFilter("chunk", (value, size) => chunk(value, size));
    this._engine.registerFilter("get_asset_value", (asset, key) => {
      var initial = get(asset, key);

      if (blank(initial)) {
        initial = get(asset, ['attributes', key]);
      }

      if (isBoolean(initial) && key === "is_active") {
        return initial ? "Active" : "Inactive";
      }

      if (/date/i.test(key) && filled(initial)) {
        return moment(initial).format('MMM dd, yyyy');
      }

      return initial;
    });

    this._engine.registerFilter("group_assets_by_asset_type", (assets) => {
      const grouped = [];

      if (blank(assets)) {
        return [];
      }

      for (const asset of assets) {
        if (get(asset, 'status_raw') == 'awaiting_completion') {
          continue;
        }

        const assetType = {
          name: asset.asset_type,
          asset_report_fields: asset.asset_report_fields,
          include_full_checklist: asset.asset_type_include_full_checklist_report_raw,
          assets: [asset],
        };

        const groupedIndex = grouped.findIndex((groupedAssetType) => lowerCase(groupedAssetType.name) == lowerCase(assetType.name));

        if (groupedIndex > -1) {
          const existing = grouped[groupedIndex];

          existing.assets.push(asset);

          grouped[groupedIndex] = existing;
        } else {
          grouped.push(assetType);
        }
      }

      return grouped;
    });

    this._engine.registerFilter("asset_report_fields", (value, exclude = []) => {
      let fields = get(value, 'asset_report_fields', []);
      let output = [];

      if (blank(fields)) {
        fields = [
          {
            id: 'serial_number',
            text: 'Serial Number',
          },
          {
            id: 'fault_details',
            text: 'Fault Details',
          },
        ];
      }

      /// prepend serial number report field if not exists
      if (!some(fields, (field) => field.id == 'serial_number')) {
        fields = [
          ... fields,
        ];
      }

      for (const field of fields) {
        if (exclude.includes(get(field, 'id'))) {
          continue;
        }

        output.push(field);
      }

      return output;
    });

    this._engine.registerFilter("snake", (value) => {
      if (blank(value) || ! isString(value)) {
        return null;
      }

      return lowerCase(value).replace(' ', '_');
    });

    this._engine.registerFilter("prompts_has_comments", (prompts) => {
      if (blank(prompts)) {
        return false;
      }

      return some(prompts, (prompt) => {
        if (get(prompt, 'type') == 'group') {
          return some(get(prompt, 'prompts', []), (sub) => filled(get(sub, 'comment')));
        }

        return filled(get(prompt, 'comment'));
      });
    });

    this._engine.registerFilter("merge_assets", (assets, checklists) => {
      let merged = assets;

      for (const checklist of checklists) {
        if (get(checklist, 'type') != 'asset') {
          continue;
        }

        for (const asset of get(checklist, 'assets', [])) {
          merged = [
            {
              ... asset,
              asset_report_fields: get(checklist, 'asset_report_fields', []),
            },
            ... merged,
          ];
        }
      }

      let uniqued = {};

      for (const asset of merged) {
        if (blank(get(uniqued, asset.id))) {
          uniqued = {
            ... uniqued,
            [asset.id]: asset,
          };
        }
      }

      return values(uniqued);
    });

    this._engine.registerFilter("filter_checklists_by_type", (checklists, type) => {
      const filtered = [];

      for (const checklist of checklists) {
        if (get(checklist, 'type') == type) {
          filtered.push(checklist);
        }
      }

      return filtered;
    });

    this._engine.registerFilter("has_checklist_report_link", (values) => {
      for (const value of values) {
        if (filled(get(value, 'checklist_report_link'))) {
          return true;
        }
      }

      return false;
    })

    this._engine.registerFilter("assets_inspection_photos", (assets) => {
      const photos = [];

      for (const asset of assets) {
        const paths = fallback(data_get(asset, 'inspection_photos'), {
          to: () => [],
        });

        const prompts = fallback(data_get(asset, 'prompts'), {
          to: () => [],
        });

        if (! data_get(asset, 'asset_type_include_full_checklist_report_raw')) {
          for (const prompt of prompts) {
            paths.push(... fallback(data_get(prompt, 'images'), {
              to: () => [],
            }));
          }
        }

        if (blank(paths)) {
          continue;
        }

        for (const path of paths) {
          let display = compute_asset_displayed_name(asset);

          if (display === data_get(asset, 'serial_number')) {
            display = `#${get(asset, 'serial_number')}`;
          }

          photos.push({
            display_name: display,
            image_path: path,
          });
        }
      }

      return photos;
    });

    this._engine.registerFilter("append_common_report_fields", (fields) => {
      const appended = [
        ... fields,
        {
          id: 'full_report_link',
          text: 'Full Report',
        },
      ];

      return appended;
    });

    this._engine.registerFilter("get_prompt_configuration_for", (configurations, prompt) => {
      let parentId = get(prompt, 'parent_id');

      if (blank(parentId)) {
        parentId = get(prompt, 'id');
      }

      if (blank(parentId)) {
        return null;
      }

      return get(configurations, parentId);
    });

    this._engine.registerFilter("get_text_color_from_hex", (color) => get_text_color_from_hex(color));

    this._engine.registerFilter("has_failed_assets", (assets) => {
      for (const asset of assets) {
        if (get(asset, "status_raw") == "fail"
          && filled(get(asset, "faults"))
        ) {
          return true;
        }
      }

      return false;
    });

    const compute_asset_displayed_name = (asset) => {
      const name = get(asset, "name");
      const serial = get(asset, "serial_number");

      if (blank(name)) {
        return serial;
      }

      if (serial == name) {
        return serial;
      }

      return `${name} (${serial})`;
    };

    this._engine.registerFilter("compute_asset_displayed_name", compute_asset_displayed_name);

    this._engine.registerFilter("exclude_pending_checklists", (checklists) => {
      const filtered = [];

      for (const checklist of checklists) {
        if (get(checklist, 'status_raw') != 'awaiting_completion') {
          filtered.push(checklist);
        }
      }

      return filtered;
    });

    this._engine.registerFilter("only_full_report_asset_checklists", (checklists) => {
      const filtered = [];

      for (const checklist of checklists) {
        const prompts = get(checklist, 'prompts');

        if (get(checklist, 'type') == 'asset'
          && get(checklist, 'asset_type_include_full_checklist_report_raw')
          && arr_some(prompts, (prompt) => ! isEqual(get(prompt, 'asset.status_raw'), 'awaiting_completion'))
        ) {
          filtered.push(checklist);
        }
      }

      return filtered;
    });

    this._engine.registerFilter("filter_new_faulty_assets", (assets) => {
      const filtered = [];

      for (const index in assets) {
        const asset = assets[index];
        const faults = this._filterNewFaults(get(asset, 'faults'));

        if (filled(faults)) {
          filtered.push({
            ... asset,
            faults,
          });
        }
      }

      return filtered;
    });

    this._engine.registerFilter("filter_old_faulty_assets", (assets) => {
      const filtered = [];

      for (const index in assets) {
        const asset = assets[index];
        const faults = this._filterOldFaults(get(asset, 'faults'));

        if (filled(faults)) {
          filtered.push({
            ... asset,
            faults,
          });
        }
      }

      return filtered;
    });

    this._engine.registerFilter("filter_new_faults", (faults) => this._filterNewFaults(faults));
    this._engine.registerFilter("filter_old_faults", (faults) => this._filterOldFaults(faults));

    this._engine.registerFilter("get_highest_severity_from_new_defects", (prompts) => {
      const faults = [];

      for (const index in prompts) {
        const prompt = prompts[index];
        const asset = get(prompt, 'asset');

        if (get(asset, 'status_raw') != "fail") {
          continue;
        }

        faults.push(... this._filterNewFaults(get(asset, 'faults')));
      }

      if (blank(faults)) {
        return null;
      }

      return first(faults);
    });

    this._engine.registerFilter("prompts_has_pass_fail_prompt", (questions) => {
      for (const index in questions) {
        const question = questions[index];

        if (this._questionsHasPassFailPrompt(get(question, 'responses'))) {
          return true;
        }
      }

      return false;
    });

    this._engine.registerFilter("chunk_asset_report_fields", (fields) => {
      if (! isArray(fields)) {
        return [];
      }

      const floor = 5;
      const ceil = 6;
      const length = fields.length;

      if (length > 6 && (length % floor) === 0) {
        return chunk(fields, floor);
      }

      return chunk(fields, ceil);
    });

    this._engine.registerFilter("is_checklist_failed", (checklist) => {
      if (data_get(checklist, 'type') !== 'asset') {
        return data_get(checklist, 'status_raw') == 'fail';
      }

      const prompts = fallback(data_get(checklist, 'prompts'), {
        to: () => [],
      });

      if (blank(prompts)) {
        return data_get(checklist, 'status_raw') == 'fail';
      }

      for (const prompt of prompts) {
        if (data_get(prompt, 'asset.status_raw') == 'fail') {
          return true;
        }
      }

      return false;
    });

    this.engine.registerFilter("is_checklist_passed", (checklist) => {
      if (data_get(checklist, 'type') !== 'asset') {
        return data_get(checklist, 'status_raw') == 'pass';
      }

      const prompts = fallback(data_get(checklist, 'prompts'), {
        to: () => [],
      });

      if (blank(prompts)) {
        return data_get(checklist, 'status_raw') == 'pass';
      }

      for (const prompt of prompts) {
        if (data_get(prompt, 'asset.status_raw') == 'pass') {
          return true;
        }
      }

      return false;
    });

    this._engine.registerFilter("to_thumbnail_presigned_url", async (value) => {
      if (blank(value)) {
        return null;
      }

      value = toString(value).replace(/\[[/]?image\]/ig, "");

      const url = await this._getURL(`resized/360_360/${value}`);

      return url;
    });
  }

  private _questionsHasPassFailPrompt(arQuestions: LooseObject[]): boolean {
    for (const index in arQuestions) {
      const question = arQuestions[index];

      if (get(question, 'type') == "pass/fail") {
        return true;
      }

      if (get(question, 'type') == 'group'
        && this._questionsHasPassFailPrompt(data_get(question, 'prompts', { default_value: [] }))
      ) {
        return true;
      }
    }

    return false;
  }

  private _filterOldFaults(arFaults: LooseObject[]): LooseObject[] {
    if (blank(arFaults)) {
      return [];
    }

    return filter(arFaults, (fault) => ! get(fault, 'is_new_fault') && ! get(fault, 'resolved'))
  }

  private _filterNewFaults(arFaults: LooseObject[]): LooseObject[] {
    if (blank(arFaults)) {
      return [];
    }

    return filter(arFaults, (fault) => get(fault, 'is_new_fault'));
  }

  private async _getURL(strFilename: string): Promise<string> {
    const result = await this._fileService.getObjectSignedUrl(strFilename, environment.client_bucket).toPromise();

    return data_get(result, 'url', { default_value: '' });
  }
}